'use client';

import React from 'react';
import styles from './CatalogSection.module.scss';
import Wrapper from '@/src/components/ui/wrapper/Wrapper';
import Label from '@/src/components/ui/label/Label';
import {useInView} from "react-intersection-observer";
import createAnimationArr from "@/src/lib/helpers/createAnimationArr";
import NextImage from "@/src/components/ui/nextImage/NextImage";

const heading = 'Expansive Catalog for Diverse Selections';
const excerpt =
    'Manage and showcase a wide variety of products, helping customers browse with ease and boosting your sales potential.';

type MainCatalogProps = {
    image: string;
    title: string;
    text: string;
};

type GridItemProps = {
    image?: string;
    video?: string;
    title: string;
    text: string;
};

const mainCatalog: MainCatalogProps = {
    image: '/images/themes/stockist/catalog/main.webp',
    title: 'Organize and Sell 2X More',
    text: 'Boost conversions and accelerate growth with a well-organized catalog designed to sell twice as much.',
};

const gridItems: GridItemProps[] = [
    {
        image: '/images/themes/stockist/catalog/pic-1.webp',
        title: 'View',
        text: 'Enhance shopping by letting customers instantly preview products and add to cart without leaving the collection page — better experience, higher sales.',
    },
    {
        image: '/images/themes/stockist/catalog/pic-2.webp',
        title: 'Sticky Filters',
        text: 'Make browsing effortless with sticky filters that remain visible as shoppers scroll, helping them find products faster and stay focused.',
    },
];

const CatalogSection = () => {

    const { ref: inViewRef2, inView: inView2 } = useInView({
        triggerOnce: true,
        rootMargin: '-250px 0px 0px',
        threshold: 0,
    });

    return (
        <section className={styles.catalogSection}>
            <Wrapper>
                <div className={styles.catalogSection__head} ref={inViewRef2}>
                    <Label>Catalog</Label>
                    <h2 className={`heading__2 ${styles.heading} ${inView2 && styles.animate__heading}`}>
                        {createAnimationArr(heading, styles.word__wrapper, 0, 0.01)}
                    </h2>
                    <p className={`excerpt ${styles.excerpt} ${inView2 && styles.animate__excerpt}`}>
                        {createAnimationArr(excerpt, styles.word__wrapper, 0.1, 0.01)}
                    </p>
                </div>

                <div className={styles['catalogSection__main']}>
                    <div className={styles['catalogSection__main-about']}>
                        <h2 className={styles['catalogSection__main-title']}>{mainCatalog.title}</h2>
                        <div className={styles['catalogSection__main-text']}>{mainCatalog.text}</div>
                    </div>
                    <div className={styles['catalogSection__main-pic']}>
                        <NextImage
                            src={mainCatalog.image}
                            alt={mainCatalog.title}
                            fill
                            sizes="(max-width: 480px) 100vw, (max-width: 1280px) 60vw, 80vw"
                            loading="lazy"
                        />
                    </div>
                </div>

                <div className={styles['catalogSection__grid']}>
                    {gridItems.map((item, idx) => (
                        <div key={idx} className={styles['catalogSection__item']}>
                            <div className={styles['catalogSection__pic']}>
                                {item.video ? (
                                    <video
                                        src={item.video}
                                        autoPlay
                                        playsInline
                                        muted
                                        loop
                                    />
                                ) : item.image ? (
                                    <NextImage
                                        src={item.image}
                                        alt={item.title}
                                        fill
                                        sizes="(max-width: 480px) 100vw, 50vw"
                                        loading="lazy"
                                    />
                                ) : null}
                            </div>
                            <h3 className={styles['catalogSection__title']}>{item.title}</h3>
                            <div className={styles['catalogSection__text']}>{item.text}</div>
                        </div>
                    ))}
                </div>
            </Wrapper>
        </section>
    );
};

export default CatalogSection;
